import { useState, useRef } from 'react';
import { X, Upload, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { passaInProgress, type RichiestaDetail } from '../../api/richieste';
import type { OmologaInline } from '../../types/omologa';

interface Props {
  richiestaId: string;
  omologa: OmologaInline | null;
  onClose: () => void;
  onSaved: (updated: RichiestaDetail) => void;
}

const inputCls =
  'w-full text-sm border border-stone-border rounded px-3 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const labelCls = 'block text-xs font-medium text-ink-soft mb-1';

function formatDateIT(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Transizione 4 (confermato → in_progress). Modello Z (TD-2):
 * - se l'omologa associata è GIÀ firmata, l'upload è opzionale: la transizione
 *   procede col firmato canonico esistente (`pdf_firmato_url`). Caricare un PDF
 *   è una SOSTITUZIONE esplicita che si riflette su tutti gli ordini che usano
 *   l'omologa (#105);
 * - se l'omologa non è ancora firmata, file + data_firma sono obbligatori.
 */
export default function InProgressModal({ richiestaId, omologa, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const giaFirmata = omologa?.stato_firma === 'firmata' && !!omologa?.pdf_firmato_url;

  const [file, setFile] = useState<File | null>(null);
  const [dataFirma, setDataFirma] = useState(
    omologa?.data_firma ?? new Date().toISOString().slice(0, 10),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function handleProcedi() {
    setError(null);
    // Upload obbligatorio solo se l'omologa NON è già firmata.
    if (!file && !giaFirmata) {
      setError(t('richieste.inprogress.val_file'));
      return;
    }
    if (file) {
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        setError(t('richieste.conferma.val_pdf'));
        return;
      }
      if (!dataFirma) {
        setError(t('richieste.inprogress.val_data'));
        return;
      }
    }
    setLoading(true);
    try {
      // Se non si carica un file (omologa già firmata) → null/null: usa l'esistente.
      const updated = await passaInProgress(
        richiestaId,
        file,
        file ? dataFirma : null,
      );
      onSaved(updated);
    } catch (err: unknown) {
      const resp = (err as { response?: { data?: { detail?: string } } })?.response;
      setError(resp?.data?.detail ?? t('richieste.inprogress.errore'));
    } finally {
      setLoading(false);
    }
  }

  const procediDisabled = loading || (!file && !giaFirmata);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark">{t('richieste.inprogress.titolo')}</h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {giaFirmata ? (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-accent-success-bg border border-[#A7F3D0] rounded-lg text-sm text-accent-success">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
              <span>
                {omologa?.data_firma
                  ? t('richieste.inprogress.gia_firmata_data', { data: formatDateIT(omologa.data_firma) })
                  : t('richieste.inprogress.gia_firmata')}
              </span>
            </div>
          ) : (
            <p className="text-sm text-ink-mid">
              {t('richieste.inprogress.descrizione')}
            </p>
          )}

          <input
            ref={fileInput}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-2 border-dashed border-stone-border rounded-lg hover:bg-stone-bg transition-colors text-ink-mid"
          >
            <Upload size={16} />
            {file
              ? t('richieste.conferma.sostituisci_pdf')
              : giaFirmata
              ? t('richieste.inprogress.carica_sostituisci')
              : t('richieste.inprogress.seleziona_pdf')}
          </button>
          {file && (
            <div className="flex items-center gap-2 px-3 py-2 bg-stone-bg/60 border border-stone-mid rounded text-sm text-ink-dark">
              <FileText size={14} className="text-ink-soft" />
              <span className="truncate flex-1">{file.name}</span>
              <span className="text-xs text-ink-soft">{(file.size / 1024).toFixed(0)} KB</span>
            </div>
          )}

          {file && giaFirmata && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-accent-warning-bg border border-[#FDE68A] rounded-lg text-xs text-accent-warning">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                <Trans i18nKey="richieste.inprogress.avviso_riutilizzo" components={{ strong: <strong /> }} />
              </span>
            </div>
          )}

          {(file || !giaFirmata) && (
            <div>
              <label className={labelCls}>
                {t('richieste.inprogress.data_firma')} <span className="text-accent-danger">*</span>
              </label>
              <input
                className={inputCls}
                type="date"
                value={dataFirma}
                onChange={(e) => setDataFirma(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>
          )}

          {error && (
            <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50"
          >
            {t('common.azioni.annulla')}
          </button>
          <button
            type="button"
            onClick={handleProcedi}
            disabled={procediDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60"
          >
            {loading ? t('richieste.inprogress.avvio_in_corso') : t('richieste.workflow.btn_in_lavorazione')}
          </button>
        </div>
      </div>
    </div>
  );
}
